import { ApiRequestError, request } from "@/api/client";
import type {
  TargetProgress,
  EmissionTrend,
  SupplierRiskScore,
  EmissionForecast,
  MonthlyBriefing,
  DashboardInsight,
  DashboardAnalyticsPayload,
  DashboardAnalyticsFilters,
  BulkUploadBatch,
  IntakeJob,
} from "../types/analytics";

export const analyticsApi = {
  getTargetProgress: async (): Promise<TargetProgress[]> => {
    return request("get", "/api/analytics/target-progress");
  },

  getTrends: async (): Promise<EmissionTrend[]> => {
    return request("get", "/api/analytics/trends");
  },

  getSupplierRisks: async (): Promise<SupplierRiskScore[]> => {
    return request("get", "/api/analytics/supplier-risks");
  },

  getForecasts: async (): Promise<EmissionForecast[]> => {
    return request("get", "/api/analytics/forecasts");
  },

  getBriefing: async (): Promise<MonthlyBriefing | null> => {
    return request("get", "/api/analytics/briefing");
  },

  getInsights: async (): Promise<DashboardInsight[]> => {
    return request("get", "/api/analytics/insights");
  },

  getDashboardAnalytics: async (
    year?: number,
    filters?: DashboardAnalyticsFilters,
  ): Promise<DashboardAnalyticsPayload> => {
    const result = await request<DashboardAnalyticsPayload>(
      "get",
      "/api/analytics/dashboard",
      {
        params: { year, ...filters },
      },
    );
    if (filters && result.scoped !== true)
      throw new Error(
        "Analytics for the selected filters are unavailable. Please retry.",
      );
    return result;
  },

  recompute: async (
    period?: "weekly" | "monthly",
  ): Promise<{ success: boolean }> => {
    return request("post", "/api/analytics/recompute", {
      data: { period },
      timeout: 120000,
    });
  },
};

export const bulkUploadApi = {
  upload: async (
    files: Array<{ s3Url: string; fileName: string }>,
    idempotencyKey: string,
    businessUnitId?: string,
    message?: string,
  ): Promise<{
    success: boolean;
    jobId: number;
    chatId: number;
    files: string[];
  }> => {
    return request("post", "/api/bulk-upload", {
      data: { files, businessUnitId, message },
      headers: { "Idempotency-Key": idempotencyKey },
      timeout: 30000,
    });
  },

  getJobs: async (): Promise<{ jobs: IntakeJob[]; total: number }> =>
    request("get", "/api/bulk-upload/jobs", {
      params: { limit: 20, offset: 0 },
    }),

  getJob: async (jobId: number): Promise<{ job: IntakeJob }> => {
    return request("get", `/api/bulk-upload/jobs/${jobId}`);
  },

  /**
   * Stops a job server-side.
   *
   * This actually cancels the work. Previously the only affordance was clearing the local
   * job reference, which stopped the polling and left the extraction running to completion —
   * indistinguishable, from the user's side, from the button not working.
   *
   * A 409 can mean confirmation or completion won the race. Read the durable state
   * so the interface never tells the user a completed upload was cancelled.
   */
  cancelJob: async (
    jobId: number,
    reason?: string,
  ): Promise<{ success: boolean; jobId: number; status: string }> => {
    try {
      return await request("post", `/api/bulk-upload/jobs/${jobId}/cancel`, {
        data: { reason },
      });
    } catch (error) {
      // The job reached a terminal state between the click and the request. The user asked
      // for it to stop and it has, so this is the outcome they wanted rather than a failure.
      if (error instanceof ApiRequestError && error.status === 409) {
        const current = await bulkUploadApi.getJob(jobId);
        return { success: false, jobId, status: current.job.status };
      }
      throw error;
    }
  },

  /**
   * Re-enqueues a job a server restart interrupted.
   *
   * The stored payload deliberately holds no bearer token. The backend revalidates the current
   * owner session and authorizes a short-lived worker credential after it reacquires the job.
   * A 409 means the job is no longer resumable.
   */
  resumeJob: async (
    jobId: number,
  ): Promise<{ success: boolean; jobId: number; status: string }> => {
    return request("post", `/api/bulk-upload/jobs/${jobId}/resume`);
  },

  getBatches: async (
    limit?: number,
    offset?: number,
  ): Promise<{
    batches: BulkUploadBatch[];
    total: number;
  }> => {
    return request("get", "/api/bulk-upload/batches", {
      params: { limit, offset },
    });
  },

  getBatchDetails: async (
    chatId: number,
  ): Promise<{
    records: unknown[];
    summary: Array<{ review_status: string; count: number }>;
  }> => {
    return request("get", `/api/bulk-upload/batches/${chatId}`);
  },
};
